/**
 * Can be used to debounce _any_ function, not limited to observables like debounceTime. It provides more control over
 * the context (this) and whether to invoke the function immediately or after the timeout. It uses Promises to return
 * the result of the original function, and allows callers to get the notification of the completion of the first/final
 * function (depending on the boolean invokeAsap).
 * @param fn - the function being debounced
 * @param timeout - timeout in milliseconds
 * @param invokeAsap - controls whether the function should be executed immediately on the first call and then ignore subsequent calls until the timeout period has passed
 * @param ctx - context of original function
 * @returns debounced function
 */
export function reductioUnum<T>(
  fn: (...args: never[]) => T,
  timeout = 200,
  invokeAsap = false,
  ctx: unknown = null
): (...args: never[]) => Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return function (this: unknown, ...args: never[]): Promise<T> {
    const context = ctx || this;
    return new Promise((resolve) => {
      invokeAsap && !timer && resolve(fn.apply(context, args));
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        invokeAsap || resolve(fn.apply(context, args));
        timer = null;
      }, timeout);
    });
  };
}
